export interface MarkdownEscapeDeps {
  escapeHtml?(value: string): string;
}

export const MARKDOWN_PUNCTUATION_CHARACTER_CLASS = '\\\\`*{}\\[\\]()#+\\-.!_>~|';

const ESCAPED_MARKDOWN_PUNCTUATION_PLACEHOLDERS = new Map<string, string>([
  ['\\', '\uE000'],
  ['`', '\uE001'],
  ['*', '\uE002'],
  ['{', '\uE003'],
  ['}', '\uE004'],
  ['[', '\uE005'],
  [']', '\uE006'],
  ['(', '\uE007'],
  [')', '\uE008'],
  ['#', '\uE009'],
  ['+', '\uE00A'],
  ['-', '\uE00B'],
  ['.', '\uE00C'],
  ['!', '\uE00D'],
  ['_', '\uE00E'],
  ['>', '\uE00F'],
  ['~', '\uE010'],
  ['|', '\uE011'],
]);

const MARKDOWN_PUNCTUATION_BY_PLACEHOLDER = new Map(
  Array.from(ESCAPED_MARKDOWN_PUNCTUATION_PLACEHOLDERS.entries()).map(([char, placeholder]) => [
    placeholder,
    char,
  ]),
);

export const ESCAPED_MARKDOWN_PUNCTUATION_PATTERN = new RegExp(
  `\\\\[${MARKDOWN_PUNCTUATION_CHARACTER_CLASS}]`,
);

const ESCAPED_MARKDOWN_PUNCTUATION_REPLACE_PATTERN = new RegExp(
  `\\\\([${MARKDOWN_PUNCTUATION_CHARACTER_CLASS}])`,
  'g',
);

const MARKDOWN_PUNCTUATION_PLACEHOLDER_PATTERN = /[\uE000-\uE011]/g;
// In `First\n \nSecond`, 0.50 removes the one-space line; mark it to restore all three paragraphs.
export const PRESERVED_EMPTY_PARAGRAPH_MARKER = '\uE012';

export function protectEscapedMarkdownPunctuation(markdown: string) {
  const protectedMarkdown = markdown.replace(
    ESCAPED_MARKDOWN_PUNCTUATION_REPLACE_PATTERN,
    (match, char: string) => ESCAPED_MARKDOWN_PUNCTUATION_PLACEHOLDERS.get(char) ?? match,
  );

  return preserveEmptyParagraphsOutsideCodeFences(protectedMarkdown);
}

function preserveEmptyParagraphsOutsideCodeFences(markdown: string) {
  let fence: { character: '`' | '~'; length: number } | null = null;

  return markdown
    .split(/(\r?\n)/)
    .map((line, index) => {
      if (index % 2 === 1) {
        return line;
      }

      if (fence) {
        const closingFence = new RegExp(`^ {0,3}\\${fence.character}{${fence.length},}[ \\t]*$`);
        if (closingFence.test(line)) {
          fence = null;
        }
        return line;
      }

      const openingFence = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
      if (openingFence) {
        const delimiter = openingFence[1];
        if (!delimiter) {
          return line;
        }
        const character = delimiter[0] as '`' | '~';
        const info = openingFence[2] ?? '';
        if (character === '~' || !info.includes('`')) {
          fence = { character, length: delimiter.length };
        }
        return line;
      }

      return line === ' ' ? PRESERVED_EMPTY_PARAGRAPH_MARKER : line;
    })
    .join('');
}

export function restoreEscapedMarkdownPunctuation(
  text: string,
  { preserveEscape = false }: { preserveEscape?: boolean } = {},
) {
  return text.replace(MARKDOWN_PUNCTUATION_PLACEHOLDER_PATTERN, (placeholder) => {
    const char = MARKDOWN_PUNCTUATION_BY_PLACEHOLDER.get(placeholder);
    if (!char) {
      return placeholder;
    }

    return preserveEscape ? `\\${char}` : char;
  });
}

export function escapeMarkdownText(value: string, deps: MarkdownEscapeDeps = {}) {
  return escapeMarkdownHtml(value, deps).replace(/[\\`*_{}[\]()#+\-.!|>~]/g, '\\$&');
}

export function escapeMarkdownLinkLabel(value: string, deps: MarkdownEscapeDeps = {}) {
  return escapeMarkdownHtml(value, deps).replace(/[\\[\]]/g, '\\$&');
}

export function escapeMarkdownImageAlt(value: string, deps: MarkdownEscapeDeps = {}) {
  return escapeMarkdownHtml(value, deps).replace(/[\\[\]]/g, '\\$&');
}

export function escapeMarkdownHtml(
  value: string,
  { escapeHtml = defaultEscapeMarkdownHtml }: MarkdownEscapeDeps = {},
) {
  return escapeHtml(value);
}

function defaultEscapeMarkdownHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
